import React, { useEffect, useRef, useState } from 'react';

import { Search as SearchIcon, X } from 'lucide-react';

import { blogPosts } from '../data/blog-posts';
import { caseStudies } from '../data/case-studies';
import { services } from '../data/services';
import { useRouterContext } from '../hooks/useRouterContext';

type SearchResult = {
  id: string | number;
  title: string;
  description?: string;
  excerpt?: string;
  type: 'service' | 'blog' | 'case-study';
  url: string;
  image?: string;
  category?: string;
  tags?: string[];
};

const SearchBar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  // Obtener el contexto del router directamente
  const routerContext = useRouterContext();
  const navigate = routerContext?.navigate;

  // Función para cerrar el buscador al hacer clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para buscar en todos los contenidos
  const searchContent = (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);

    // Normalizar la consulta para búsqueda insensible a mayúsculas/minúsculas y acentos
    const normalizedQuery = searchQuery
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    // Buscar en servicios
    const serviceResults: SearchResult[] = Object.values(services)
      .flat()
      .filter(service => {
        const normalizedTitle = service.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        const normalizedDesc = service.description
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        return (
          normalizedTitle.includes(normalizedQuery) || normalizedDesc.includes(normalizedQuery)
        );
      })
      .map(service => ({
        id: service.id,
        title: service.title,
        description: service.description,
        type: 'service' as const,
        url: `/servicios/${service.id}`,
        category: service.category,
      }));

    // Buscar en blogs
    const blogResults: SearchResult[] = blogPosts
      .filter(post => {
        const normalizedTitle = post.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        const normalizedExcerpt =
          post.excerpt
            ?.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') || '';
        const normalizedContent =
          post.content
            ?.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') || '';
        return (
          normalizedTitle.includes(normalizedQuery) ||
          normalizedExcerpt.includes(normalizedQuery) ||
          normalizedContent.includes(normalizedQuery)
        );
      })
      .map(post => ({
        id: post.id,
        title: post.title,
        description: post.excerpt,
        type: 'blog' as const,
        url: `/blog/${post.slug}`,
        image: post.image,
        category: post.category,
        tags: post.tags,
      }));

    // Buscar en casos de éxito
    const caseStudyResults: SearchResult[] = caseStudies
      .filter(study => {
        const normalizedTitle = study.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        const normalizedDesc = study.description
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        return (
          normalizedTitle.includes(normalizedQuery) || normalizedDesc.includes(normalizedQuery)
        );
      })
      .map(study => ({
        id: study.id,
        title: study.title,
        description: study.description,
        type: 'case-study' as const,
        url: `/casos-de-exito/${study.id}`,
        image: study.image,
        category: study.industry,
        tags: study.tags,
      }));

    // Combinar y ordenar resultados
    const allResults = [...serviceResults, ...blogResults, ...caseStudyResults];

    // Ordenar por relevancia (coincidencia en título tiene más peso)
    const sortedResults = allResults.sort((a, b) => {
      const aTitle = a.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      const bTitle = b.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      const aTitleMatch = aTitle.includes(normalizedQuery);
      const bTitleMatch = bTitle.includes(normalizedQuery);

      if (aTitleMatch && !bTitleMatch) return -1;
      if (!aTitleMatch && bTitleMatch) return 1;
      return 0;
    });

    setResults(sortedResults);
    setIsLoading(false);
  };

  // Buscar cuando cambia la consulta
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      searchContent(query);
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [query]);

  // La función handleSearch no es necesaria ya que se usa setQuery directamente en el onChange

  // Manejar la navegación a la página de resultados
  const handleViewAllResults = () => {
    if (query.trim()) {
      // Guardar búsqueda en localStorage para compartir con SearchResults
      const savedSearches = localStorage.getItem('recentSearches');
      let recentSearches: string[] = savedSearches ? JSON.parse(savedSearches) : [];
      recentSearches = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(recentSearches));

      // Verificar si navigate está disponible
      if (navigate) {
        navigate(`/buscar?q=${encodeURIComponent(query)}`);
      } else {
        // Fallback a window.location si navigate no está disponible
        window.location.href = `/buscar?q=${encodeURIComponent(query)}`;
      }
      setIsOpen(false);
      setQuery('');
    }
  };

  // Manejar clic en un resultado
  const handleResultClick = (url: string) => {
    // Guardar búsqueda en localStorage para compartir con SearchResults
    if (query.trim()) {
      const savedSearches = localStorage.getItem('recentSearches');
      let recentSearches: string[] = savedSearches ? JSON.parse(savedSearches) : [];
      recentSearches = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
    }

    // Verificar si navigate está disponible
    if (navigate) {
      navigate(url);
    } else {
      // Fallback a window.location si navigate no está disponible
      window.location.href = url;
    }
    setIsOpen(false);
    setQuery('');
  };

  // Resaltar coincidencias en el texto
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;

    const normalizedText = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const normalizedQuery = query
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const index = normalizedText.toLowerCase().indexOf(normalizedQuery);
    if (index === -1) return text;

    const before = text.substring(0, index);
    const match = text.substring(index, index + query.length);
    const after = text.substring(index + query.length);

    return (
      <>
        {before}
        <span className="bg-yellow-200 text-gray-900">{match}</span>
        {after}
      </>
    );
  };

  return (
    <div className="relative" ref={searchRef}>
      {/* Botón de búsqueda */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-[#EAEAEA] hover:text-white focus:outline-none"
        aria-label="Buscar"
      >
        <SearchIcon className="h-5 w-5" />
      </button>

      {/* Modal de búsqueda */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="p-4">
            <form
              onSubmit={e => {
                e.preventDefault();
                handleViewAllResults();
              }}
              className="w-full"
            >
              <div className="flex items-center border-b border-gray-300 pb-2">
                <SearchIcon className="h-5 w-5 text-gray-500 mr-2" />
                <input
                  type="text"
                  placeholder="Buscar servicios, blogs, casos de éxito..."
                  className="w-full outline-none text-gray-700"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleViewAllResults();
                    }
                  }}
                  autoFocus
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              <button type="submit" className="hidden">
                Buscar
              </button>
            </form>

            {/* Resultados de búsqueda */}
            <div className="mt-4 max-h-80 overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Buscando...</p>
                </div>
              ) : results.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {results.slice(0, 5).map(result => (
                      <div
                        key={`${result.type}-${result.id}`}
                        className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                        onClick={() => handleResultClick(result.url)}
                      >
                        <div className="flex items-start">
                          {result.image && (
                            <div className="flex-shrink-0 h-10 w-10 rounded overflow-hidden mr-3">
                              <img
                                src={result.image}
                                alt={result.title}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          )}
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {highlightMatch(result.title, query)}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {result.description && highlightMatch(result.description, query)}
                            </p>
                            <div className="mt-1 flex items-center">
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                {result.type === 'service'
                                  ? 'Servicio'
                                  : result.type === 'blog'
                                    ? 'Blog'
                                    : 'Caso de Éxito'}
                              </span>
                              {result.category && (
                                <span className="text-xs text-gray-500 ml-2">
                                  {result.category}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {results.length > 5 && (
                    <div className="mt-3 pt-2 border-t border-gray-200">
                      <button
                        onClick={handleViewAllResults}
                        className="w-full text-center text-sm text-blue-600 hover:text-blue-800 py-2"
                      >
                        Ver todos los resultados ({results.length})
                      </button>
                    </div>
                  )}
                </>
              ) : query ? (
                <div className="text-center py-4">
                  <p className="text-gray-500">No se encontraron resultados para "{query}"</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
